// ── Native full-screen alarm sync (Android) ──────────────────────────────────
// JS can't run once the app is fully closed, so exact wake-ups for
// reminderType === "alarm" items have to be handed off to Android's
// AlarmManager ahead of time. Whenever the relevant data changes (or the app
// comes back to the foreground), this recomputes every alarm occurrence due
// in the next few weeks and hands the whole list to the native
// AlarmScheduler plugin, which diffs it against what's already scheduled and
// adjusts AlarmManager accordingly. No-ops entirely outside a native build.
//
// This is the PRIMARY ring mechanism and works correctly whenever the phone
// isn't frozen by an OEM app-freezer (see AlarmGuardService's class comment)
// — which is most of the time. The freeze case (AlarmManager registrations
// getting force-stopped out from under the app) is handled separately, via
// a server-sent FCM push that ClarityMessagingService.java rings from
// independently — see that file's class comment.
import { useEffect, useRef } from 'react';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { collectUpcomingAlarms } from '../utils/taskLogic';
import { collectUpcomingHabitAlarms } from '../utils/habitLogic';

const AlarmScheduler = registerPlugin('AlarmScheduler');
const isNative = Capacitor.isNativePlatform();

// The alarm's full-screen popup rides on a system notification (needed to
// pop it over the lock screen / while the app isn't foregrounded), which on
// Android 13+ needs the POST_NOTIFICATIONS runtime permission. The app's own
// "Enable Notifications" toggle in Settings normally covers this — but only
// once the user has also gone through sign-in/push registration. Rather than
// have alarms silently fail to show for anyone who hasn't done that, ask for
// it directly the first time there's actually an alarm to schedule. Only
// prompts once; if already granted or denied, this is a no-op check.
let permissionRequested = false;
async function ensureNotificationPermission() {
  if (permissionRequested) return;
  try {
    const status = await PushNotifications.checkPermissions();
    if (status.receive !== 'granted') await PushNotifications.requestPermissions();
    permissionRequested = true;
  } catch { /* best-effort */ }
}

// Live hex values for the current theme, so the native ringing screen can
// match whatever theme the user has picked instead of one fixed palette.
function currentThemeColors() {
  const style = getComputedStyle(document.documentElement);
  const get = (name, fallback) => (style.getPropertyValue(name) || '').trim() || fallback;
  return {
    accent: get('--c-text-accent', '#CD9FA0'),
    primaryLight: get('--c-primary-light', '#e6a4c4'),
    primaryDeep: get('--c-primary-deep', '#b3577e'),
    onPrimary: get('--c-on-primary', '#ffffff'),
  };
}

function syncAlarms(tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips) {
  const alarms = [
    ...collectUpcomingAlarms({ tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides }),
    ...collectUpcomingHabitAlarms({ habits, habitLogs, habitSkips }),
  ];
  // eslint-disable-next-line no-console
  console.log(`[ClarityAlarm] syncAlarms: ${alarms.length} upcoming alarm(s)`, alarms.map(a => ({ title: a.title, triggerAt: new Date(a.triggerAt).toString() })));
  if (alarms.length) ensureNotificationPermission();
  const payload = JSON.stringify({ alarms, colors: currentThemeColors() });
  AlarmScheduler.scheduleAll({ json: payload })
    // eslint-disable-next-line no-console
    .then(() => console.log('[ClarityAlarm] scheduleAll resolved'))
    // eslint-disable-next-line no-console
    .catch((e) => console.error('[ClarityAlarm] scheduleAll failed', e));
}

export function useAlarmSync(tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits = [], habitLogs = {}, habitSkips = []) {
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isNative) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      syncAlarms(tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips);
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips]);

  useEffect(() => {
    if (!isNative) return;
    const sub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) syncAlarms(tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips);
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
